import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { of } from 'rxjs';
import { concatAll, map, mergeAll } from 'rxjs/operators';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { DeviceDetailsComponent } from './device-details/device-details.component';
import { DeviceSettingsService } from './device-settings.service';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { IUpdateDeviceParams } from './device-setting.interfaces';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-device-settings',
  templateUrl: './device-settings.component.html',
  styleUrls: ['./device-settings.component.scss'],
})
export class DeviceSettingsComponent implements OnInit {
  @ViewChild(DeviceDetailsComponent) accountDetailRef:
    | DeviceDetailsComponent
    | undefined;

  formGroup!: FormGroup;
  readonly LANG_CONST = LANG_CONST;
  CONSTANTS = CONSTANTS;
  stepper = 1;
  SuccessMessage = '';
  deviceId!: string;
  doorDeviceId!: string;
  deviceName!: string;
  deviceType!: string;
  unitId!: string;
  unitName!: string;
  accessPointId!: string;
  accessPointName!: string;
  parentType!: string;
  userList!: IUser[] | undefined;
  batteryPercentage: string = '';
  activationState: string = '';

  /**
   * @description constructor injects all necesary services
   * @param { AddUserComponent } dialogRef
   */
  constructor(
    public dialogRef: MatDialogRef<DeviceDetailsComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: any,
    private deviceSettingService: DeviceSettingsService,
    private localizationService: LocalizationService
  ) {
    this.parentType = data.parentType;
    this.batteryPercentage = data?.batteryPercentage;
    this.activationState = data?.activationState;
    if (this.parentType === CONSTANTS.UNIT) {
      this.deviceId = data?.deviceId;
      this.deviceName = data?.deviceName;
      this.unitId = data?.unitId;
      this.unitName = data?.unitName;
      this.parentType = data?.parentType;
    } else if (this.parentType === CONSTANTS.ACCESSPOINT) {
      this.deviceId = data?.deviceId;
      this.doorDeviceId = data?.doorDeviceId;
      this.deviceName = data?.deviceName;
      this.deviceType = data?.deviceType;
      this.accessPointId = data?.accessPointId;
      this.accessPointName = data?.accessPointName;
      this.parentType = data?.parentType;
    }
  }

  /**
   * @description this is onInit life cycle method
   */
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.createControls();
    this.mapControls(this.parentType);
  }

  /**
   * @description Create all child component into parents
   * @returns void
   */
  createControls(): void {
    this.formGroup = this.fb.group({
      deviceDetails: this.fb.group({
        lockSound: [],
        autoLock: [],
        autoLockTime: [],
        usersAccess: [''],
        deviceInfo: [''],
        firmwareUpdate: [],
        helpFaq: [''],
        factoryReset: [''],
        unitId: [''],
        unitName: [''],
        accessPointId: [''],
        accessPointName: [''],
        deviceId: [''],
        doorDeviceId: [''],
        deviceName: [''],
        activities: [''],
        deviceType: [''],
        deviceMode: [''],
        enabled: [''],
        deviceOpenedState: [''],
        updatedAt: [''],
        updatedTime: [''],
      }),
      deviceInfo: this.fb.group({
        appVersion: [''],
        batteryPercentage: [''],
        bluetoothVersion: [''],
        deviceId: [''],
        deviceName: [''],
        deviceType: [''],
        firmwareVersion: [''],
        updatedAt: [''],
        updatedTime: [''],
        hardwareVariant: [''],
        hardwareVersion: [''],
        latitude: [''],
        lockVersion: [''],
        deviceStatus: [''],
        deviceState: [''],
        longitude: [''],
        macAddress: [''],
        modelNumber: [''],
        activationDate: [''],
        manufactureDate: [''],
        serialNumber: [''],
        sku: [''],
        deviceKind: [''],
        productType: [''],
        imageName: [''],
      }),
    });
  }

  /**
   * @description close modal dialogue with help of material service
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  /**
   * @description #mapControls calls the getDeviceInfo & getUsersByDevice APIs
   *              and map the form with associated values.
   * @returns void
   */
  mapControls(type: string): void {
    if (type === CONSTANTS.UNIT) {
      this.getDeviceInfoUsers();
    } else if (type === CONSTANTS.ACCESSPOINT) {
      this.getPartnerDeviceInfoUsers();
      this.getPartnerDeviceDoorType();
    }
  }
  getPartnerDeviceInfoUsers(): void {
    const getPartnerDeviceInfo = this.deviceSettingService
      .getPartnerDeviceInfo(this.deviceId)
      .pipe(
        map((res: any) => {
          const partnerDeviceInfo = res?.getPartnerDevice;
          this.formGroup.patchValue({
            deviceDetails: {
              accessPointId: this.accessPointId,
              accessPointName: this.accessPointName,
              deviceId: this.deviceId,
              deviceName: partnerDeviceInfo?.deviceName,
              usersAccess: '0', // In case of no users
              deviceInfo: partnerDeviceInfo?.deviceInfo,
              fimwareUpdate: partnerDeviceInfo?.fimwareUpdate,
              updatedAt: partnerDeviceInfo?.updatedAt,
              updatedTime: partnerDeviceInfo?.updatedTime,
              activities: partnerDeviceInfo?.activities,
              deviceType: partnerDeviceInfo?.deviceType,
              enabled: partnerDeviceInfo?.enabled,
            },
            deviceInfo: {
              appVersion: partnerDeviceInfo?.appVersion,
              batteryPercentage: partnerDeviceInfo?.batteryPercentage,
              bluetoothVersion: partnerDeviceInfo?.bluetoothVersion,
              deviceId: this.deviceId,
              deviceName: partnerDeviceInfo?.deviceName,
              deviceType: partnerDeviceInfo?.deviceType,
              firmwareVersion: partnerDeviceInfo?.firmwareVersion,
              updatedAt: partnerDeviceInfo?.updatedAt,
              updatedTime: partnerDeviceInfo?.updatedTime,
              hardwareVariant: partnerDeviceInfo?.hardwareVariant,
              latitude: partnerDeviceInfo?.latitude,
              lockVersion: partnerDeviceInfo?.lockVersion,
              longitude: partnerDeviceInfo?.longitude,
              macAddress: partnerDeviceInfo?.macAddress,
              modelNumber: partnerDeviceInfo?.modelNumber,
              activationDate: partnerDeviceInfo?.activationDate,
              manufactureDate: partnerDeviceInfo?.manufacturingDate,
              deviceState: partnerDeviceInfo?.deviceState,
              deviceStatus: partnerDeviceInfo?.deviceStatus,
              serialNumber: this.deviceId,
              deviceKind: partnerDeviceInfo?.deviceKind,
              productType: partnerDeviceInfo?.productType || 'N/A',
            },
          });
        })
      );

    const getUsersByPartnerDevice = this.deviceSettingService
      .getUsersByPartnerDevice(this.accessPointId)
      .pipe(
        map((res: any) => {
          this.userList = res;
          this.formGroup.patchValue({
            deviceDetails: {
              usersAccess: res?.length.toString(),
            },
          });
        })
      );

    of(getPartnerDeviceInfo, getUsersByPartnerDevice)
      .pipe(concatAll())
      .subscribe();
  }

  /**
   * @description #getPartnerDeviceDoorType calls the getPartnerDevice API
   * with the deviceId of deviceType door and map the res with the required associated values.
   * @returns void
   */
  getPartnerDeviceDoorType(): void {
    this.deviceSettingService
      .getPartnerDeviceInfo(this.doorDeviceId)
      .pipe(
        map((res: any) => {
          const partnerDeviceInfo = res?.getPartnerDevice;
          this.formGroup.patchValue({
            deviceDetails: {
              doorDeviceId: this.doorDeviceId,
              deviceMode: partnerDeviceInfo?.deviceMode,
              deviceOpenedState: partnerDeviceInfo?.deviceOpenedState,
            },
          });
        })
      )
      .subscribe();
  }

  getDeviceInfoUsers(): void {
    const getDeviceInfo = this.deviceSettingService
      .getDeviceInfo(this.deviceId)
      .pipe(
        map((res: any) => {
          const deviceInfo = res?.getDeviceInfo;
          this.formGroup.patchValue({
            deviceDetails: {
              unitId: this.unitId,
              unitName: this.unitName,
              deviceId: deviceInfo?.deviceId,
              deviceName: deviceInfo?.deviceName,
              lockSound: deviceInfo?.buzzerEnabled,
              usersAccess: '0', // In case of no users
              autoLockTime: deviceInfo?.autoLockTime,
              deviceInfo: deviceInfo?.deviceInfo,
              fimwareUpdate: deviceInfo?.fimwareUpdate,
              updatedAt: deviceInfo?.updatedAt,
              updatedTime: deviceInfo?.updatedTime,
              autoLock: deviceInfo?.autoLockEnabled,
            },
            deviceInfo: {
              appVersion: deviceInfo?.appVersion,
              batteryPercentage: deviceInfo?.batteryPercentage,
              bluetoothVersion: deviceInfo?.bluetoothVersion,
              deviceId: deviceInfo?.deviceId,
              deviceName: deviceInfo?.deviceName,
              deviceType: deviceInfo?.deviceType,
              firmwareVersion: deviceInfo?.firmwareVersion,
              updatedAt: deviceInfo?.updatedAt,
              updatedTime: deviceInfo?.updatedTime,
              hardwareVariant: deviceInfo?.hardwareVariant,
              hardwareVersion: deviceInfo?.hardwareVersion || null,
              latitude: deviceInfo?.latitude,
              lockVersion: deviceInfo?.lockVersion,
              longitude: deviceInfo?.longitude,
              macAddress: deviceInfo?.macAddress,
              modelNumber: deviceInfo?.modelNumber,
              activationDate: deviceInfo?.activationDate,
              manufactureDate: deviceInfo?.manufacturingDate,
              deviceState: deviceInfo?.deviceState,
              deviceStatus: deviceInfo?.deviceStatus,
              serialNumber: deviceInfo?.deviceId,
              sku: deviceInfo?.sku,
              productType: deviceInfo?.productType || 'N/A',
              imageName: deviceInfo?.imageName,
            },
          });
        })
      );
    const getUsersbyDevice = this.deviceSettingService
      .getUsersByDevice(this.deviceId)
      .pipe(
        map((res: any) => {
          this.userList = res?.getUsersByDevice;
          this.formGroup.patchValue({
            deviceDetails: {
              usersAccess: res?.getUsersByDevice?.length.toString(),
            },
          });
        })
      );

    of(getDeviceInfo, getUsersbyDevice).pipe(concatAll()).subscribe();
  }

  updateDeviceInfoSyc($event: IUpdateDeviceParams) {
    if ($event?.deviceName) {
      this.deviceName = $event?.deviceName;
    }
    if ($event?.deviceType) {
      this.formGroup.patchValue({
        deviceInfo: { deviceType: $event?.deviceType },
      });
      this.deviceType = $event?.deviceType;
    }
    if ($event?.parentType) this.parentType = $event?.parentType;
  }
}
